<script setup>
import { ref, computed } from 'vue'
import { useStore } from 'vuex'
import { useRoute } from 'vue-router'
import { API, HOST, HOST_TULUM } from '@/utils/http'
import { formatTime } from '@/utils/datetime'
import { toast } from '@/components/ui/toast'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Spinner } from '@/components/ui/spinner'
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from '@/components/ui/collapsible'
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover'
import {
  CheckCircle2,
  Clock,
  Ban,
  CircleHelp,
  Cloud,
  ShoppingCart,
  ImageOff,
  RefreshCw,
  Eye,
  EyeClosed,
  ChevronDown
} from 'lucide-vue-next'
import {
  TASKING_STATUS_LABELS,
  getCaptureTime,
  getCaptureFootprint,
  getEvaluationStatus,
  getOrderStatusLabel,
  useTaskingStatus,
  useTaskingHover
} from '@/composables/useTasking'

const props = defineProps({
  layer: {
    type: Object,
    required: true
  },
  activeTab: {
    type: String,
    default: 'data'
  }
})

const store = useStore()
const route = useRoute()
const orgId = computed(() => route.params.org_id)
const settings = computed(() => store.getters['user/getUserSettings'])

// Tasking data - layer structure from store is layer.properties.layer
const taskingLayer = computed(() => {
  return props.layer?.properties?.layer || props.layer?.layer || {}
})

const taskingCode = computed(() => {
  return taskingLayer.value?.code
})

const taskingData = computed(() => {
  return taskingLayer.value?.data || {}
})

// Captures from layer data (already loaded in vuex store)
const layerCaptures = computed(() => {
  return taskingLayer.value?.captures || []
})

// Tasking status
const currentStatus = computed(() => taskingLayer.value?.status ?? 1)
const { isPendingApproval, isRejected, isInProgress, isDone, isFailed, isCancelled } = useTaskingStatus(currentStatus)
const { hoveredCaptureId, setHoveredCapture, clearHoveredCapture } = useTaskingHover()

const orderType = computed(() => taskingData.value?.order_type || 'IMAGE')

const captureWindow = computed(() => {
  const start = taskingData.value?.start_time
  const end = taskingData.value?.end_time
  const tz = settings.value?.tz || 'UTC'

  if (start && end) {
    return `${formatTime(start, tz, 'D MMM YYYY')} - ${formatTime(end, tz, 'D MMM YYYY')}`
  }
  return null
})

// Captures - use layer data as primary source, sorted by publish time descending
const captures = computed(() => {
  return [...layerCaptures.value].sort((a, b) => {
    const timeA = getCaptureTime(a)
    const timeB = getCaptureTime(b)
    if (!timeA && !timeB) return 0
    if (!timeA) return 1
    if (!timeB) return -1
    return new Date(timeB).getTime() - new Date(timeA).getTime()
  })
})
const capturesLoading = ref(false)
const orderingCapture = ref(null)
const expandedCaptures = ref(new Set())

const toggleCaptureDetails = (captureId) => {
  if (expandedCaptures.value.has(captureId)) {
    expandedCaptures.value.delete(captureId)
  } else {
    expandedCaptures.value.add(captureId)
  }
}

const isCaptureExpanded = (captureId) => {
  return expandedCaptures.value.has(captureId)
}

// Filter state
const activeFilter = ref('fulfilling')

// Track which captures are hidden on map (stored in layer properties)
const hiddenCaptures = computed(() => {
  return props.layer?.properties?.hiddenCaptures || []
})

const publishedCaptures = computed(() => {
  return captures.value.filter(c => c.status === 'PUBLISHED')
})

const validCaptures = computed(() => {
  return publishedCaptures.value.filter(c => c.evaluation_status !== 'INVALID' && c.strip_id)
})

// Filter counts
const fulfillingCaptures = computed(() => {
  return captures.value.filter(c => c.fulfilling === true)
})

const bestAttemptsCaptures = computed(() => {
  return captures.value.filter(c => {
    const evaluation = getEvaluationStatus(c)
    return evaluation !== 'INVALID' && c.status === 'PUBLISHED'
  })
})

const invalidCaptures = computed(() => {
  return captures.value.filter(c => getEvaluationStatus(c) === 'INVALID')
})

// Filtered captures based on active filter
const filteredCaptures = computed(() => {
  switch (activeFilter.value) {
    case 'fulfilling':
      return fulfillingCaptures.value
    case 'best':
      return bestAttemptsCaptures.value
    case 'invalid':
      return invalidCaptures.value
    default:
      return captures.value
  }
})

// Whether the layer itself is visible in the sidebar
const isLayerVisible = computed(() => props.layer?.properties?.visible ?? true)

// Check if a capture is visible on map (must also consider layer visibility)
const isCaptureVisible = (captureId) => {
  return isLayerVisible.value && !hiddenCaptures.value.includes(captureId)
}

// Check if all filtered captures (current tab) are visible
const areAllFilteredCapturesVisible = computed(() => {
  if (!isLayerVisible.value) return false
  return filteredCaptures.value.every(c => !hiddenCaptures.value.includes(c.id))
})

// Toggle visibility of captures in the current tab only
const toggleAllCapturesVisibility = () => {
  const filteredIds = filteredCaptures.value.map(c => c.id)
  const currentHidden = [...hiddenCaptures.value]

  if (areAllFilteredCapturesVisible.value) {
    // Hide current tab's captures - add their IDs to hidden list (preserving others)
    const newHidden = [...new Set([...currentHidden, ...filteredIds])]
    store.dispatch('layers/updateLayerProperties', {
      orgId: orgId.value,
      layerId: props.layer?.uuid,
      properties: { hiddenCaptures: newHidden }
    })
  } else {
    // Show current tab's captures - remove their IDs from hidden list (preserving others)
    const filteredIdSet = new Set(filteredIds)
    const newHidden = currentHidden.filter(id => !filteredIdSet.has(id))

    // Auto-enable the layer when showing captures and the layer is hidden
    const properties = { hiddenCaptures: newHidden }
    if (!isLayerVisible.value) {
      properties.visible = true
    }

    store.dispatch('layers/updateLayerProperties', {
      orgId: orgId.value,
      layerId: props.layer?.uuid,
      properties
    })
  }
}

// Toggle capture visibility on map
const toggleCaptureVisibility = (capture) => {
  const captureId = capture.id
  const currentHidden = [...hiddenCaptures.value]
  const isShowing = currentHidden.includes(captureId)

  if (isShowing) {
    // Remove from hidden list (show)
    const index = currentHidden.indexOf(captureId)
    currentHidden.splice(index, 1)
  } else {
    // Add to hidden list (hide)
    currentHidden.push(captureId)
  }

  // Auto-enable the layer when showing a capture and the layer is hidden
  const properties = { hiddenCaptures: currentHidden }
  if (isShowing && !isLayerVisible.value) {
    properties.visible = true
  }

  // Update layer properties in store
  store.dispatch('layers/updateLayerProperties', {
    orgId: orgId.value,
    layerId: props.layer?.uuid,
    properties
  })
}

const refreshLayer = async () => {
  if (!orgId.value || !taskingCode.value) return

  try {
    capturesLoading.value = true

    // First, refresh captures from Planet API (with geometry)
    try {
      await API({
        method: 'POST',
        url: `${HOST_TULUM}/tasking/${orgId.value}/layer/${taskingCode.value}/refresh-captures`
      })
    } catch (refreshError) {
      console.warn('Could not refresh captures from Planet:', refreshError)
      toast({
        title: 'Refresh failed',
        description: refreshError.response?.data?.detail || 'Could not refresh captures from Planet. Displayed data may be out of date.',
        variant: 'destructive'
      })
    }

    // Then reload layers from store to get the updated captures
    await store.dispatch('layers/fetchOrgLayers', { orgId: orgId.value })
  } catch (error) {
    console.error('Error refreshing layer:', error)
  } finally {
    capturesLoading.value = false
  }
}

const orderCapture = async (capture) => {
  if (!capture.item_ids?.length || orderingCapture.value) return

  try {
    orderingCapture.value = capture.id

    const response = await API({
      method: 'POST',
      url: `${HOST}/org/${orgId.value}/layers/from-capture`,
      data: {
        tasking_code: taskingCode.value,
        capture_id: capture.id,
        item_ids: capture.item_ids,
        item_type: capture.item_type,
        strip_id: capture.strip_id,
        capture_time: capture.capture_time
      }
    })

    if (response.data) {
      toast({
        title: 'Order created',
        description: response.data.message || 'Order created - pending approval',
        variant: 'default'
      })

      // Re-fetch all layers so the store is updated with the new order data
      await store.dispatch('layers/fetchOrgLayers', { orgId: orgId.value })
    }
  } catch (error) {
    console.error('Error ordering capture:', error)
    toast({
      title: 'Order failed',
      description: error.response?.data?.detail || 'Failed to create order',
      variant: 'destructive'
    })
  } finally {
    orderingCapture.value = null
  }
}

const formatCloudCover = (cloudCover) => {
  if (cloudCover === null || cloudCover === undefined) return null
  return `${Math.round(cloudCover * 100)}%`
}

const canOrderCapture = (capture) => {
  return (
    capture.status === 'PUBLISHED' &&
    capture.item_ids?.length > 0 &&
    getEvaluationStatus(capture) !== 'INVALID' &&
    orderingCapture.value !== capture.id
  )
}

// Map captures to their hi-res orders (newest order per capture wins)
const captureOrderMap = computed(() => {
  const orders = [...(taskingLayer.value?.orders || [])]
  orders.sort((a, b) => b.id - a.id)
  const map = {}
  for (const order of orders) {
    const captureId = order.data?.capture_id
    if (captureId && !map[captureId]) map[captureId] = order
  }
  return map
})

const getCaptureOrder = (captureId) => captureOrderMap.value[captureId] || null
const isCaptureHiRes = (captureId) => getCaptureOrder(captureId)?.status === 0
</script>

<template>
  <div class="space-y-4">
    <!-- Error Display -->
    <div v-if="isFailed && taskingLayer?.error" class="bg-red-50 border border-red-200 rounded p-3">
      <div class="text-sm text-red-700">
        {{ taskingLayer.error?.message || taskingLayer.error?.error || 'An error occurred' }}
      </div>
    </div>

    <!-- Captures Section -->
    <div>
      <div class="flex items-center justify-between mb-3">
        <h4 class="text-sm font-medium">Captures ({{ captures.length }})</h4>
        <div class="flex items-center gap-1">
          <Button
            v-if="captures.length > 0"
            variant="ghost"
            size="sm"
            @click="toggleAllCapturesVisibility"
            :title="areAllFilteredCapturesVisible ? 'Hide all on map' : 'Show all on map'"
          >
            <Eye v-if="areAllFilteredCapturesVisible" class="w-4 h-4 text-primary" />
            <EyeClosed v-else class="w-4 h-4 text-black" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            @click="refreshLayer"
            :disabled="capturesLoading"
          >
            <RefreshCw :class="['w-4 h-4', { 'animate-spin': capturesLoading }]" />
          </Button>
        </div>
      </div>

      <!-- Filter Tabs -->
      <div v-if="captures.length > 0" class="flex flex-wrap gap-2 mb-3">
        <Button
          :variant="activeFilter === 'all' ? 'default' : 'outline'"
          size="sm"
          @click="activeFilter = 'all'"
        >
          All ({{ captures.length }})
        </Button>
        <Button
          :variant="activeFilter === 'fulfilling' ? 'default' : 'outline'"
          size="sm"
          @click="activeFilter = 'fulfilling'"
        >
          Fulfilling ({{ fulfillingCaptures.length }})
        </Button>
        <Button
          :variant="activeFilter === 'best' ? 'default' : 'outline'"
          size="sm"
          @click="activeFilter = 'best'"
        >
          Best attempts ({{ bestAttemptsCaptures.length }})
        </Button>
        <Button
          :variant="activeFilter === 'invalid' ? 'default' : 'outline'"
          size="sm"
          @click="activeFilter = 'invalid'"
        >
          Invalid ({{ invalidCaptures.length }})
        </Button>
      </div>

      <!-- Loading State -->
      <div v-if="capturesLoading && captures.length === 0" class="flex items-center justify-center py-8">
        <Spinner class="w-6 h-6" />
        <span class="ml-2 text-sm text-muted-foreground">Loading captures...</span>
      </div>

      <!-- No Captures -->
      <div v-else-if="captures.length === 0" class="text-center py-8 text-muted-foreground">
        <ImageOff class="w-8 h-8 mx-auto mb-2 opacity-50" />
        <p class="text-sm">No captures available yet</p>
        <p v-if="isInProgress" class="text-xs mt-1">Captures will appear as they become available</p>
      </div>

      <!-- No Results for Filter -->
      <div v-else-if="filteredCaptures.length === 0" class="text-center py-8 text-muted-foreground">
        <p class="text-sm">No captures match this filter</p>
      </div>

      <!-- Captures List -->
      <div v-else class="space-y-3">

        <Card
          v-for="capture in filteredCaptures"
          :key="capture.id"
          class="overflow-hidden"
          :class="{ 'border-primary': hoveredCaptureId === capture.id }"
          @mouseenter="setHoveredCapture(capture.id)"
          @mouseleave="clearHoveredCapture()"
        >
          <div class="p-3 space-y-2">
            <!-- Header: Strip ID + Fulfilling Badge + Actions -->
            <div class="flex items-center justify-between">
              <div class="flex items-center gap-1.5 min-w-0">
                <span class="font-medium text-sm truncate">
                  {{ getCaptureTime(capture) ? formatTime(getCaptureTime(capture), null, 'D MMMM YYYY HH:mm') : (capture.strip_id || capture.id) }}
                </span>
              </div>
              <div class="flex items-center gap-1 shrink-0">
                <!-- Hi-Res Popover -->
                <Popover v-if="canOrderCapture(capture) || getCaptureOrder(capture.id)">
                  <PopoverTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      :class="isCaptureHiRes(capture.id) ? 'text-primary font-medium' : 'text-muted-foreground'"
                    >
                      <Spinner v-if="getCaptureOrder(capture.id) && (getCaptureOrder(capture.id).status > 0 || getCaptureOrder(capture.id).status === 100)" class="w-4 h-4 mr-1" />
                      <CheckCircle2 v-else-if="isCaptureHiRes(capture.id)" class="w-4 h-4 mr-1" />
                      <RefreshCw v-else-if="getCaptureOrder(capture.id) && getCaptureOrder(capture.id).status < 0" class="w-4 h-4 mr-1" />
                      <CircleHelp v-else class="w-4 h-4 mr-1" />
                      Hi-Res
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent class="w-64 p-3">
                    <!-- No order yet -->
                    <template v-if="!getCaptureOrder(capture.id)">
                      <p class="text-sm mb-2">Order imagery to view full resolution tiles for this capture.</p>
                      <Button size="sm" @click="orderCapture(capture)" :disabled="orderingCapture === capture.id">
                        <Spinner v-if="orderingCapture === capture.id" class="w-3 h-3 mr-1" />
                        Order
                      </Button>
                    </template>
                    <!-- Order in progress (including pending approval) -->
                    <template v-else-if="getCaptureOrder(capture.id).status > 0 || getCaptureOrder(capture.id).status === 100">
                      <div class="flex items-center">
                        <Spinner class="w-5 h-5 mr-2" />
                        <div>
                          <h4 class="font-medium text-sm">Order Processing</h4>
                          <p class="text-sm text-muted-foreground">{{ getOrderStatusLabel(getCaptureOrder(capture.id).status) }}</p>
                        </div>
                      </div>
                    </template>
                    <!-- Order completed -->
                    <template v-else-if="getCaptureOrder(capture.id).status === 0">
                      <div class="flex items-center">
                        <CheckCircle2 class="w-5 h-5 mr-2 text-primary" />
                        <div>
                          <h4 class="font-medium text-sm text-primary">Order Complete</h4>
                        </div>
                      </div>
                    </template>
                    <!-- Order failed/rejected → Try Again -->
                    <template v-else>
                      <p class="text-sm mb-2">The previous order failed. You can place a new one.</p>
                      <Button size="sm" @click="orderCapture(capture)" :disabled="orderingCapture === capture.id">
                        <Spinner v-if="orderingCapture === capture.id" class="w-3 h-3 mr-1" />
                        <RefreshCw v-else class="w-3 h-3 mr-1" />
                        Try Again
                      </Button>
                    </template>
                  </PopoverContent>
                </Popover>
                <!-- Eye icon to toggle visibility on map -->
                <Button
                  variant="ghost"
                  size="sm"
                  class="h-8 w-8 p-0"
                  @click="toggleCaptureVisibility(capture)"
                  :disabled="!getCaptureFootprint(capture)"
                  :title="!getCaptureFootprint(capture) ? 'Footprint not available' : (isCaptureVisible(capture.id) ? 'Hide on map' : 'Show on map')"
                >
                  <Eye v-if="isCaptureVisible(capture.id) && getCaptureFootprint(capture)" class="w-4 h-4 text-primary" />
                  <EyeClosed v-else class="w-4 h-4 text-black" />
                </Button>
              </div>
            </div>

            <!-- Always-visible info -->
            <div class="space-y-1 text-xs text-muted-foreground">
              <div v-if="capture.fulfilling === true">
                Fulfilling: Yes
              </div>
              <div v-if="capture.status">
                Status: {{ capture.status }}
              </div>

              <!-- Cloud Cover -->
              <div v-if="formatCloudCover(capture.cloud_cover)">
                Cloud cover: {{ formatCloudCover(capture.cloud_cover) }}
              </div>

              <!-- View angle -->
              <div v-if="capture.view_angle_max != null">
                View angle: {{ capture.view_angle_max }}&deg;
              </div>
            </div>

            <!-- Collapsible technical details -->
            <Collapsible :open="isCaptureExpanded(capture.id)">
              <CollapsibleTrigger class="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground cursor-pointer py-1" @click="toggleCaptureDetails(capture.id)">
                <ChevronDown :class="['w-3 h-3 transition-transform', { 'rotate-180': isCaptureExpanded(capture.id) }]" />
                Details
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div class="space-y-1 text-xs text-muted-foreground pt-1">
                  <div v-if="capture.acquired_time">
                    Acquired: {{ formatTime(capture.acquired_time, null, 'D MMMM YYYY HH:mm') }}
                  </div>
                  <div>
                    Evaluation:
                    <span
                      :class="getEvaluationStatus(capture) === 'INVALID' ? 'text-red-500 font-medium' : ''"
                    >
                      {{ getEvaluationStatus(capture) }}
                    </span>
                  </div>
                  <div v-if="capture.assessment">Assessment: {{ capture.assessment }}</div>
                  <div v-if="capture.satellite_type">Satellite type: {{ capture.satellite_type }}</div>
                  <div v-if="capture.item_type">Item type: {{ capture.item_type }}</div>
                  <div v-if="capture.item_ids?.length">Item IDs: {{ capture.item_ids.join(', ') }}</div>
                  <div v-if="capture.strip_id">Strip ID: {{ capture.strip_id }}</div>
                  <div v-if="capture.ground_id">Ground ID: {{ capture.ground_id }}</div>
                  <div v-if="capture.id">Capture ID: {{ capture.id }}</div>
                </div>
              </CollapsibleContent>
            </Collapsible>

            <!-- Messages for non-orderable captures -->
            <div
              v-if="getEvaluationStatus(capture) === 'INVALID'"
              class="text-xs text-muted-foreground"
            >
              Cannot order - evaluation invalid
            </div>
            <div
              v-else-if="(!capture.item_ids || capture.item_ids.length === 0) && capture.status === 'PUBLISHED'"
              class="text-xs text-muted-foreground"
            >
              Not available for ordering yet
            </div>
          </div>
        </Card>
      </div>
    </div>
  </div>
</template>
